import React from 'react';
import { motion } from 'framer-motion';
import { Megaphone } from 'lucide-react';
import { MARKETING_FEATURES } from '../constants';

const MARKETING_IMAGES = [
  '/img/person-phone.jpg',
  '/img/video-ai.jpg',
  '/img/reviews.jpg',
  '/img/person-restaurant.jpg',
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export const MarketingSection: React.FC = () => {
  return (
    <section
      id="marketing-ia"
      className="py-24 relative overflow-hidden"
      style={{ background: '#ffffff' }}
    >
      {/* Emerald blur */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-400/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Hero image */}
        <div className="relative rounded-2xl overflow-hidden mb-16">
          <img
            src="/persona-mujer-oficina.jpg"
            alt=""
            className="w-full h-[240px] md:h-[360px] object-cover"
            loading="lazy"
            style={{ objectPosition: '50% 40%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-white/30" />

          {/* Content on top of image */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 py-1.5 px-5 rounded-full text-xs font-bold mb-4 uppercase tracking-[0.2em]"
              style={{
                background: 'rgba(60,179,113,0.15)',
                border: '1px solid rgba(60,179,113,0.3)',
                color: '#2d8653',
              }}
            >
              <Megaphone size={14} />
              Marketing con IA
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-black text-slate-900 mb-3 font-display"
              style={{ fontSize: 'clamp(1.6rem, 5vw, 3.5rem)', letterSpacing: '-0.02em', lineHeight: 1.1 }}
            >
              Y cuando todo funcione,<br />
              hacemos crecer tu clientela
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-slate-700 font-medium leading-relaxed text-sm md:text-lg max-w-xl"
            >
              Instagram, campañas WhatsApp, reseñas Google y contenido que se genera solo.
            </motion.p>
          </div>
        </div>

        {/* Feature Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {MARKETING_FEATURES.map((feature, idx) => (
            <motion.div
              key={idx}
              variants={cardVariants}
              whileHover={{ y: -8, scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              className="rounded-2xl flex flex-col h-full group transition-all duration-300 hover:-translate-y-2 overflow-hidden"
              style={{
                background: '#ffffff',
                border: '1px solid rgba(0,0,0,0.07)',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              }}
            >
              {/* Card image */}
              <div className="h-40 overflow-hidden relative bg-slate-800 flex-shrink-0">
                <img
                  src={MARKETING_IMAGES[idx]}
                  alt={feature.title}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/10 to-transparent" />
                {/* Highlight badge on image */}
                {feature.highlight && (
                  <div
                    className="absolute bottom-3 left-3 text-xs font-bold px-3 py-1 rounded-full text-white"
                    style={{
                      background: 'rgba(60, 179, 113, 0.85)',
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    {feature.highlight}
                  </div>
                )}
              </div>

              {/* Card content */}
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-emerald-700 transition-colors leading-snug">
                  {feature.title}
                </h3>
                <p className="text-slate-500 text-sm flex-grow leading-relaxed font-light">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Showcase Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mb-16 rounded-2xl p-8 md:p-12"
          style={{
            background: 'linear-gradient(135deg, rgb(15, 23, 42), rgb(30, 41, 59))',
            border: '1px solid rgba(255, 255, 255, 0.05)',
          }}
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-white mb-2">Así se ve en tu negocio</h3>
            <p className="text-slate-400 text-sm font-light">Tu chatbot WhatsApp gestionando reservas y tu panel de control con métricas reales</p>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            <div className="flex-shrink-0 bg-slate-800 rounded-2xl" style={{ maxWidth: '240px' }}>
              <img
                src="/img/whatsapp-mockup.png"
                alt="Chatbot WhatsApp gestionando una reserva"
                className="w-full rounded-2xl"
                loading="lazy"
                style={{ boxShadow: 'rgba(0, 0, 0, 0.3) 0px 20px 60px' }}
              />
            </div>
            <div className="bg-slate-800 rounded-2xl" style={{ maxWidth: '560px' }}>
              <img
                src="/img/dashboard-mockup.jpg"
                alt="Panel de control con métricas de reservas y actividad"
                className="w-full rounded-2xl"
                loading="lazy"
                style={{ boxShadow: 'rgba(0, 0, 0, 0.3) 0px 20px 60px' }}
              />
            </div>
          </div>
        </motion.div>

        {/* Price comparison */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="rounded-2xl p-8 md:p-10 text-center"
          style={{
            background: 'linear-gradient(135deg, rgb(240, 253, 244), rgb(236, 253, 245))',
            border: '1px solid rgba(60, 179, 113, 0.15)',
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
            <div>
              <div className="text-3xl font-black text-slate-900 mb-1">500-1.500€</div>
              <div className="text-sm text-slate-500">Agencia tradicional</div>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-2xl">→</span>
            </div>
            <div>
              <div className="text-3xl font-black mb-1" style={{ color: '#3CB371' }}>Desde 9,99€</div>
              <div className="text-sm text-slate-500">Con Ancora + IA</div>
            </div>
          </div>
          <p className="text-slate-600 text-sm font-light">
            Mismo resultado (o mejor). Incluido en todos los planes.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
